import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useAttachImageToTask } from "@coworker/apprestructured/src/tasks/hooks/useAttachImageToTask";
import { useUpdateTaskMutation } from "@coworker/apprestructured/src/tasks/hooks/useUpdateTaskById";
import { Image } from "@coworker/apprestructured/src/tasks/types/imageUpload";
import { OVERVIEW_TASKS_QUERY_KEY } from "@coworker/apprestructured/src/TasksOverview/constants/queryKeys";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { Trans } from "@coworker/locales";
import { Task } from "@coworker/types/lib/tasks/base";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { useCompleteWithChanges } from "../../hooks/TaskActions/useCompleteWithChanges";
import { useCreateTask } from "../../hooks/TaskActions/useCreateTask";
import { usePrepareForm } from "../../hooks/TaskActions/usePrepareForm";
import { useInputPopup } from "../InputPopup";

type FormMode = "create" | "edit" | "complete";

interface TaskFormProps {
  config: any;
  task_id: string;
  formType: string;
  formMode: FormMode;
  baseTask?: Task;
}

export function useTaskForm({
  config,
  task_id,
  formType,
  formMode,
  baseTask,
}: TaskFormProps) {
  const unique_form_id = useMemo(() => {
    return crypto.randomUUID();
  }, []);
  const navigate = useFixaNavigate();
  const { toggleModal } = React.useContext(ModalContext);
  const useBeforeCreate = config.useBeforeCreate || (() => () => true);
  const usePostCreate = config.usePostCreate || (() => () => true);
  const { closePopup } = useInputPopup();
  const { showToast } = useToastNotification();
  const useReactToChanges = config.useReactToChanges || (() => null);
  const isRepeatableTask = formType === "planned";
  const dbCollection = isRepeatableTask ? "repeatable_tasks" : "tasks";
  const taskPath = `${dbCollection}/${task_id}`;
  const isCreate = formMode === "create";
  const queryClient = useQueryClient();
  const { preparedForm, images, isFormValid, editFields, task, form } =
    usePrepareForm({
      baseTask,
      task_id,
      config,
      formType,
      isCreate,
    });
  useReactToChanges(task_id, preparedForm, editFields);

  const beforeCreate = useBeforeCreate({ form: preparedForm });
  const postCreate = usePostCreate({ form: preparedForm });

  const { call: createTask, createTaskMutation } = useCreateTask();
  const updateTask = useUpdateTaskMutation();
  const addImageToTask = useAttachImageToTask(task_id);
  const createTaskAction = async () => {
    if (!(await beforeCreate())) {
      return;
    }
    const newTaskId = await createTask(preparedForm, images, unique_form_id);

    if (formType === "product_quality") {
      showToast(
        config.createCurtainMessage || <Trans>curtainAddedString</Trans>
      );
    } else {
      showToast(
        config.createCurtainMessage || <Trans>curtainAddedString</Trans>,
        <Trans>curtainViewString</Trans>,
        () => {
          toggleModal({
            type: ModalType.TaskDetail,
            payload: { taskId: newTaskId, taskType: preparedForm?.task_type },
          });
          closePopup();
        }
      );
    }

    if (postCreate()) {
      navigate(-1);
    }
  };

  const formFinished = React.useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const updateTaskAction = React.useCallback(async () => {
    await updateTask.mutateAsync({ data: preparedForm, taskId: task_id });

    const toAdd = images.filter((image: any) => !image?.id) as Image[];
    if (toAdd.length) {
      await addImageToTask.mutateAsync({ images: toAdd });
    }

    await queryClient.invalidateQueries({
      queryKey: [OVERVIEW_TASKS_QUERY_KEY],
    });

    showToast(
      <Trans>curtainUpdateString</Trans>,
      <Trans>curtainViewString</Trans>,
      () => {
        toggleModal({
          type: ModalType.TaskDetail,
          payload: { taskId: task_id, taskType: preparedForm?.task_type },
        });
      }
    );

    formFinished();
  }, [
    addImageToTask,
    images,
    updateTask,
    task_id,
    preparedForm,
    showToast,
    formFinished,
    toggleModal,
    queryClient,
  ]);

  const { call: completeWithChanges, updateTaskMutation } =
    useCompleteWithChanges(preparedForm, task_id);
  const completeWithChangesAction = React.useCallback(async () => {
    const data = await completeWithChanges();
    if (!data) return;
    formFinished();
  }, [completeWithChanges, formFinished]);

  return {
    isFormValid,
    createTaskMutation,
    createTaskAction,
    updateTaskAction,
    completeWithChangesAction,
    updateTaskMutation,
    preparedForm,
    form,
    editFields,
    taskPath,
    formMode,
    task,
    images,
  };
}
